import { createFileRoute } from '@tanstack/react-router';
import { HStack, Separator, Text, VStack } from 'ui_2';
import { QuestBentos } from '@src/components/quests/QuestBentos';
import { z } from 'zod';
import * as Tabs from '@radix-ui/react-tabs';
import parc_img from '@assets/parc.png';
import { LeaderboardPageContents } from '@src/components/leaderboard/LeaderboardPageContents';
import { HomePageContents } from '@src/components/home/HomePageContents';
import { ReferralQuestRow } from '@src/components/quests/ReferralQuestRow';
import { Balances } from '@src/components/Balances';
import { ConnectOrAccount } from '@src/components/ConnectOrAccount';
import { useDappState } from '@src/hooks/useDappState';
import { Body } from '@src/components/layout/Body';
import { ProBannerAd } from '@src/components/ProBannerAd';
import { FriendPageContents } from '@src/components/friends/FriendsPageContents';

type Tab = 'home' | 'friends' | 'leaderboard' | 'quests';

export const tabs_search = z.object({
  tab: z
    .enum(['home', 'friends', 'leaderboard', 'quests'])
    .optional()
    .default('home'),
});

export const Route = createFileRoute('/')({
  staticData: {
    title: 'Home',
  },
  validateSearch: tabs_search,
  component: RouteComponent,
});

const headerXPadding = `px-4 sm:px-8 md:px-16`;
const headerYPadding = `py-4 md:py-8`;

function RouteComponent() {
  const { tab } = Route.useSearch();
  const navigate = Route.useNavigate();

  const getTabClassName = (tabValue?: Tab) =>
    `h-8 w-auto px-3 border-b-2 flex items-center justify-center whitespace-nowrap ${
      tab === tabValue ? 'border-b-black' : 'border-b-transparent'
    }`;

  const tabItems: { value: Tab; label: string }[] = [
    { value: 'home', label: 'Home' },
    { value: 'friends', label: '👯 Friends' },
    { value: 'leaderboard', label: '🏆 Leaderboard' },
    { value: 'quests', label: 'Quests' },
  ];

  const { isPuzzleWalletWebview } = useDappState();

  return (
    <div className='pb-safe flex h-dvh min-h-dvh flex-col'>
      <Tabs.Root
        value={tab as Tab}
        onValueChange={(value) =>
          navigate({
            to: Route.to,
            search: {
              tab: value as Tab,
            },
          })
        }
        className='flex h-full flex-col'
      >
        <VStack className='pt-safe sticky top-0 z-50 w-full bg-white text-left'>
          <HStack
            className={`w-full items-center gap-2 ${headerXPadding} ${headerYPadding}`}
          >
            <HStack className='flex-grow' />
            <Balances />
            <ConnectOrAccount showOnConnected={false} />
          </HStack>
          <HStack
            className={`w-full ${headerXPadding} max-w-full overflow-x-auto`}
          >
            <Tabs.List className='flex w-full min-w-max flex-row items-center gap-1 pr-4 sm:pr-8 md:pr-16'>
              {tabItems.map(({ value, label }) => (
                <Tabs.Trigger
                  key={value}
                  value={value}
                  className={getTabClassName(value)}
                >
                  <div className='flex items-center gap-2'>
                    {value === 'quests' && (
                      <img src={parc_img} className='h-4 w-4 object-contain' />
                    )}
                    <Text font='bold-sm'>{label}</Text>
                  </div>
                </Tabs.Trigger>
              ))}
            </Tabs.List>
          </HStack>
          <Separator className='w-full bg-gray-100' />
        </VStack>
        <Body showFooter={!isPuzzleWalletWebview}>
          <VStack className='w-full gap-4'>
            <Tabs.Content value='home' className='justify-items-center'>
              <HomePageContents />
            </Tabs.Content>
            <Tabs.Content value='friends' className='justify-items-center'>
              <FriendPageContents />
            </Tabs.Content>
            <Tabs.Content value='leaderboard' className='justify-items-center'>
              <LeaderboardPageContents />
            </Tabs.Content>
            <Tabs.Content value='quests' className='justify-items-center'>
              <VStack className='items-center gap-4'>
                <ReferralQuestRow />
                <QuestBentos />
              </VStack>
            </Tabs.Content>
          </VStack>
        </Body>
      </Tabs.Root>
      <ProBannerAd />
    </div>
  );
}
